/**
 * Estate Flow - Financial Analytics Dashboard
 * Mobile-responsive with Export & QuickBooks/Sage Integration
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, Home, Users, Calendar,
  PieChart as PieChartIcon, BarChart3, Activity, Target, AlertTriangle,
  CheckCircle, Clock, Percent, ArrowUpRight, ArrowDownRight, RefreshCw,
  Download, Filter, Wallet, Calculator, Building2, FileSpreadsheet,
  Link2, ExternalLink, FileText, Settings, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  primary: '#10b981',
  secondary: '#6366f1',
  accent: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  purple: '#8b5cf6',
  pink: '#ec4899',
  teal: '#14b8a6',
};

const INCOME_COLORS = ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5'];
const EXPENSE_COLORS = ['#ef4444', '#f87171', '#fca5a5', '#fecaca', '#fee2e2'];

const FinancialAnalytics = ({ orgId, orgName, orgType = 'landlord' }) => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('12months');
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [connections, setConnections] = useState([]);
  const [connectingProvider, setConnectingProvider] = useState(null);

  // Analytics data
  const [revenueData, setRevenueData] = useState([]);
  const [incomeBreakdown, setIncomeBreakdown] = useState([]);
  const [expenseBreakdown, setExpenseBreakdown] = useState([]);
  const [propertyPerformance, setPropertyPerformance] = useState([]);
  const [kpis, setKpis] = useState({});
  const [cashFlow, setCashFlow] = useState([]);
  const [occupancyData, setOccupancyData] = useState([]);
  const [taxSummary, setTaxSummary] = useState(null);

  useEffect(() => {
    fetchAnalyticsData();
    fetchConnections();
  }, [orgId, period]);

  useEffect(() => {
    const refreshFinancials = () => fetchAnalyticsData();
    window.addEventListener('estateflow:financials-updated', refreshFinancials);
    return () => window.removeEventListener('estateflow:financials-updated', refreshFinancials);
  }, [orgId, period]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchConnections = async () => {
    if (!orgId) return;
    try {
      const res = await axios.get(`${API}/accounting/connections/${orgId}`, {
        headers: getAuthHeaders()
      });
      setConnections(res.data.connections || []);
    } catch (e) {
      // silent - connections may not exist yet
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Choose endpoint based on org type
      const endpoint = orgType === 'hoa' 
        ? `${API}/hoa/analytics?period=${period}`
        : orgType === 'property_manager'
        ? `${API}/property-manager/analytics?period=${period}`
        : `${API}/landlord/analytics?period=${period}`;
      
      try {
        const res = await fetch(endpoint, { headers });
        if (res.ok) {
          const data = await res.json();
          
          if (orgType === 'hoa' && data.kpis) {
            // HOA analytics — real data from hoa_dues
            setKpis(data.kpis);
            setRevenueData(data.revenue_data || []);
            setIncomeBreakdown(data.income_breakdown || []);
            setExpenseBreakdown(data.expense_breakdown || []);
            setPropertyPerformance([]);
            setCashFlow([]);
            setOccupancyData([]);
            setTaxSummary({
              estimatedTax: 0, taxableIncome: data.kpis.totalRevenue || 0,
              deductions: data.kpis.totalExpenses || 0,
              effectiveRate: 0, quarterlyPayment: 0, nextDeadline: '', potentialSavings: 0
            });
          } else if (data.kpis) {
            // Landlord / PM analytics — same unified format
            setKpis(data.kpis);
            setRevenueData(data.revenue_data || []);
            setIncomeBreakdown(data.income_breakdown || []);
            setExpenseBreakdown(data.expense_breakdown || []);
            setPropertyPerformance([]);
            setCashFlow([]);
            setOccupancyData([]);
            setTaxSummary({
              estimatedTax: 0, taxableIncome: data.kpis.totalRevenue || 0,
              deductions: data.kpis.totalExpenses || 0,
              effectiveRate: 0, quarterlyPayment: 0, nextDeadline: '', potentialSavings: 0
            });
          } else if (data.total_collected > 0 || data.total_tenants > 0) {
            // Landlord data — build from dashboard response
            const revenue = data.total_collected || 0;
            const tenants = data.total_tenants || 0;
            const units = data.total_units || 0;
            setKpis({
              totalRevenue: revenue, totalExpenses: 0, netProfit: revenue,
              profitMargin: '100.0', avgOccupancy: units > 0 ? Math.round(tenants/units*100) : 0,
              collectionRate: 0, avgDaysToLease: 0, maintenanceCostPerUnit: 0,
              yoyGrowth: 0, vacancyLoss: 0, totalUnits: units, occupiedUnits: tenants
            });
            setRevenueData([]);
            setIncomeBreakdown([]);
            setExpenseBreakdown([]);
          } else {
            setEmptyState();
          }
        } else {
          setEmptyState();
        }
      } catch (e) {
        setEmptyState();
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setEmptyState();
    } finally {
      setLoading(false);
    }
  };

  const setEmptyState = () => {
    setRevenueData([]);
    setIncomeBreakdown([]);
    setExpenseBreakdown([]);
    setPropertyPerformance([]);
    setKpis({
      totalRevenue: 0, totalExpenses: 0, netProfit: 0,
      profitMargin: '0.0', avgOccupancy: 0, collectionRate: 0,
      avgDaysToLease: 0, maintenanceCostPerUnit: 0,
      yoyGrowth: 0, vacancyLoss: 0, totalUnits: 0, occupiedUnits: 0
    });
    setCashFlow([]);
    setOccupancyData([]);
    setTaxSummary({
      estimatedTax: 0, taxableIncome: 0, deductions: 0,
      effectiveRate: 0, quarterlyPayment: 0, nextDeadline: '',
      potentialSavings: 0
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value);
  };

  const handleExport = async (format, reportType = 'general_ledger') => {
    setExporting(true);
    try {
      const token = localStorage.getItem('access_token');
      let url = '';
      let filename = '';

      if (format === 'quickbooks') {
        url = `${API}/export/${orgId || 'demo'}/quickbooks`;
        filename = `estateflow_quickbooks.iif`;
      } else if (format === 'sage') {
        url = `${API}/export/${orgId || 'demo'}/sage`;
        filename = `estateflow_sage.csv`;
      } else {
        url = `${API}/export/${orgId || 'demo'}/csv?report_type=${reportType}`;
        filename = `estateflow_${reportType}.csv`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Exported ${format === 'quickbooks' ? 'QuickBooks IIF' : format === 'sage' ? 'Sage CSV' : 'CSV'} file`, {
        description: `${filename} downloaded successfully`
      });
      setShowExportDialog(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed', { description: 'Please try again' });
    } finally {
      setExporting(false);
    }
  };

  const handleConnect = async (provider) => {
    setConnectingProvider(provider);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/accounting/connect`, {
        provider,
        organization_id: orgId || 'demo'
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`${provider === 'quickbooks' ? 'QuickBooks' : 'Sage'} connection initiated`, {
        description: 'Settings saved. Full OAuth integration coming soon.'
      });
      fetchConnections();
    } catch (error) {
      toast.error('Connection failed');
    } finally {
      setConnectingProvider(null);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 sm:p-3 border rounded-lg shadow-lg text-xs sm:text-sm">
          <p className="font-semibold text-gray-900">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const maxEarnings = Math.max(...(revenueData.map(d => d.revenue) || [100]));

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="financial-analytics">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center">
            <Activity className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-emerald-600" />
            Financial Analytics
          </h2>
          <p className="text-sm text-gray-600">{orgName || 'Property Portfolio'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 sm:w-40 text-xs sm:text-sm" data-testid="period-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3months">Last 3 Months</SelectItem>
              <SelectItem value="6months">Last 6 Months</SelectItem>
              <SelectItem value="12months">Last 12 Months</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalyticsData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>

          {/* Export Dialog */}
          <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-btn">
                <Download className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  <FileSpreadsheet className="h-5 w-5 mr-2 text-emerald-600" />
                  Export Financial Data
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <p className="text-sm text-gray-600">Choose your export format:</p>

                {/* CSV Export Options */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-800">General CSV</h4>
                  <div className="grid grid-cols-1 gap-2">
                    <Button
                      variant="outline" className="justify-start text-left h-auto py-2"
                      onClick={() => handleExport('csv', 'general_ledger')}
                      disabled={exporting}
                      data-testid="export-general-ledger"
                    >
                      <FileText className="h-4 w-4 mr-2 text-blue-600 shrink-0" />
                      <div>
                        <p className="font-medium text-sm">General Ledger</p>
                        <p className="text-xs text-gray-500">All transactions with debits/credits</p>
                      </div>
                    </Button>
                    <Button
                      variant="outline" className="justify-start text-left h-auto py-2"
                      onClick={() => handleExport('csv', 'income_statement')}
                      disabled={exporting}
                      data-testid="export-income-statement"
                    >
                      <FileText className="h-4 w-4 mr-2 text-emerald-600 shrink-0" />
                      <div>
                        <p className="font-medium text-sm">Income Statement</p>
                        <p className="text-xs text-gray-500">Revenue and expenses by category</p>
                      </div>
                    </Button>
                  </div>
                </div>

                {/* Accounting Software Exports */}
                <div className="space-y-2 pt-2 border-t">
                  <h4 className="text-sm font-semibold text-gray-800">Accounting Software</h4>
                  <div className="grid grid-cols-1 gap-2">
                    <Button
                      variant="outline" className="justify-start text-left h-auto py-2"
                      onClick={() => handleExport('quickbooks')}
                      disabled={exporting}
                      data-testid="export-quickbooks"
                    >
                      <div className="w-5 h-5 mr-2 rounded bg-green-600 text-white flex items-center justify-center text-xs font-bold shrink-0">Q</div>
                      <div>
                        <p className="font-medium text-sm">QuickBooks IIF</p>
                        <p className="text-xs text-gray-500">Import directly into QuickBooks Desktop</p>
                      </div>
                    </Button>
                    <Button
                      variant="outline" className="justify-start text-left h-auto py-2"
                      onClick={() => handleExport('sage')}
                      disabled={exporting}
                      data-testid="export-sage"
                    >
                      <div className="w-5 h-5 mr-2 rounded bg-emerald-700 text-white flex items-center justify-center text-xs font-bold shrink-0">S</div>
                      <div>
                        <p className="font-medium text-sm">Sage 50 CSV</p>
                        <p className="text-xs text-gray-500">Sage-compatible import format</p>
                      </div>
                    </Button>
                  </div>
                </div>

                {exporting && (
                  <div className="flex items-center justify-center py-2 text-sm text-gray-600">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating export...
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI Cards - Mobile: 2 cols, Tablet: 3 cols, Desktop: 6 cols */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-emerald-600 font-medium truncate">Total Revenue</p>
                <p className="text-base sm:text-xl font-bold text-emerald-700">{formatCurrency(kpis.totalRevenue)}</p>
                <div className="flex items-center text-[10px] sm:text-xs text-emerald-600 mt-0.5">
                  <ArrowUpRight className="h-3 w-3 mr-0.5" />
                  <span>+{kpis.yoyGrowth}%</span>
                </div>
              </div>
              <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-500 opacity-50 shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-red-600 font-medium truncate">Total Expenses</p>
                <p className="text-base sm:text-xl font-bold text-red-700">{formatCurrency(kpis.totalExpenses)}</p>
                <p className="text-[10px] sm:text-xs text-red-500 mt-0.5">
                  {kpis.totalRevenue > 0 ? `${((kpis.totalExpenses / kpis.totalRevenue) * 100).toFixed(1)}% of revenue` : 'No revenue yet'}
                </p>
              </div>
              <TrendingDown className="h-6 w-6 sm:h-8 sm:w-8 text-red-500 opacity-50 shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-blue-600 font-medium truncate">Net Profit</p>
                <p className="text-base sm:text-xl font-bold text-blue-700">{formatCurrency(kpis.netProfit)}</p>
                <p className="text-[10px] sm:text-xs text-blue-500 mt-0.5">{kpis.profitMargin}% margin</p>
              </div>
              <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500 opacity-50 shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-purple-600 font-medium truncate">Occupancy</p>
                <p className="text-base sm:text-xl font-bold text-purple-700">{kpis.avgOccupancy}%</p>
                <p className="text-[10px] sm:text-xs text-purple-500 mt-0.5">{kpis.occupiedUnits}/{kpis.totalUnits}</p>
              </div>
              <Home className="h-6 w-6 sm:h-8 sm:w-8 text-purple-500 opacity-50 shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-amber-600 font-medium truncate">Collection</p>
                <p className="text-base sm:text-xl font-bold text-amber-700">{kpis.collectionRate}%</p>
                <p className="text-[10px] sm:text-xs text-amber-500 mt-0.5">On-time</p>
              </div>
              <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-amber-500 opacity-50 shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-teal-600 font-medium truncate">Days to Lease</p>
                <p className="text-base sm:text-xl font-bold text-teal-700">{kpis.avgDaysToLease}</p>
                <p className="text-[10px] sm:text-xs text-teal-500 mt-0.5">Avg: 30</p>
              </div>
              <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-teal-500 opacity-50 shrink-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs - Scrollable on mobile */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-2 px-2">
          <TabsList className="inline-flex w-auto min-w-full sm:w-full sm:grid sm:grid-cols-6">
            <TabsTrigger value="overview" className="text-xs sm:text-sm whitespace-nowrap">Overview</TabsTrigger>
            <TabsTrigger value="income" className="text-xs sm:text-sm whitespace-nowrap">Income</TabsTrigger>
            <TabsTrigger value="expenses" className="text-xs sm:text-sm whitespace-nowrap">Expenses</TabsTrigger>
            <TabsTrigger value="properties" className="text-xs sm:text-sm whitespace-nowrap">Properties</TabsTrigger>
            <TabsTrigger value="tax" className="text-xs sm:text-sm whitespace-nowrap">Tax</TabsTrigger>
            <TabsTrigger value="integrations" className="text-xs sm:text-sm whitespace-nowrap" data-testid="integrations-tab">
              <Link2 className="h-3 w-3 mr-1" />QB/Sage
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="pb-2 sm:pb-6">
              <CardTitle className="flex items-center text-sm sm:text-base">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-emerald-600" />
                Revenue vs Expenses
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">Monthly financial performance</CardDescription>
            </CardHeader>
            <CardContent className="px-1 sm:px-6">
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#6b7280" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#6b7280" tickFormatter={(v) => `$${v/1000}k`} tick={{ fontSize: 10 }} width={45} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2}
                    fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" />
                  <Area type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2}
                    fillOpacity={1} fill="url(#colorExpenses)" name="Expenses" />
                  <Line type="monotone" dataKey="profit" stroke="#6366f1" strokeWidth={2}
                    strokeDasharray="5 5" name="Profit" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader className="pb-2 sm:pb-6">
                <CardTitle className="flex items-center text-sm sm:text-base">
                  <Wallet className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600" />
                  Cash Flow Forecast
                </CardTitle>
              </CardHeader>
              <CardContent className="px-1 sm:px-6">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={cashFlow}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" stroke="#6b7280" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#6b7280" tickFormatter={(v) => `$${v/1000}k`} tick={{ fontSize: 10 }} width={40} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="inflow" fill="#10b981" name="Cash In" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="outflow" fill="#ef4444" name="Cash Out" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 sm:pb-6">
                <CardTitle className="flex items-center text-sm sm:text-base">
                  <Home className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-purple-600" />
                  Occupancy Rate Trend
                </CardTitle>
              </CardHeader>
              <CardContent className="px-1 sm:px-6">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={occupancyData}>
                    <defs>
                      <linearGradient id="colorOccupancy" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" stroke="#6b7280" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#6b7280" domain={[80, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} width={35} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Area type="monotone" dataKey="rate" stroke="#8b5cf6" strokeWidth={2}
                      fillOpacity={1} fill="url(#colorOccupancy)" name="Occupancy Rate" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Income Tab */}
        <TabsContent value="income" className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-sm sm:text-base">
                  <PieChartIcon className="h-4 w-4 mr-2 text-emerald-600" />
                  Income Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="px-1 sm:px-6">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={incomeBreakdown} cx="50%" cy="50%"
                      innerRadius={50} outerRadius={85} paddingAngle={2}
                      dataKey="value" label={({ percentage }) => `${percentage}%`}
                      labelLine={false}>
                      {incomeBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={INCOME_COLORS[index % INCOME_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm sm:text-base">Income Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {incomeBreakdown.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <div className="flex items-center min-w-0">
                        <div className="w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                          style={{ backgroundColor: INCOME_COLORS[idx % INCOME_COLORS.length] }} />
                        <span className="text-gray-700 truncate">{item.name}</span>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <p className="font-semibold">{formatCurrency(item.value)}</p>
                        <p className="text-xs text-gray-500">{item.percentage}%</p>
                      </div>
                    </div>
                  ))}
                  <div className="border-t pt-3 flex justify-between font-bold text-sm">
                    <span>Total Income</span>
                    <span className="text-emerald-600">
                      {formatCurrency(incomeBreakdown.reduce((sum, i) => sum + i.value, 0))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-sm sm:text-base">
                  <PieChartIcon className="h-4 w-4 mr-2 text-red-600" />
                  Expense Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="px-1 sm:px-6">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={expenseBreakdown} cx="50%" cy="50%"
                      innerRadius={50} outerRadius={85} paddingAngle={2}
                      dataKey="value" label={({ percentage }) => `${percentage}%`}>
                      {expenseBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm sm:text-base">Expense Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {expenseBreakdown.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <div className="flex items-center min-w-0">
                        <div className="w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                          style={{ backgroundColor: EXPENSE_COLORS[idx % EXPENSE_COLORS.length] }} />
                        <span className="text-gray-700 truncate">{item.name}</span>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <p className="font-semibold">{formatCurrency(item.value)}</p>
                        <p className="text-xs text-gray-500">{item.percentage}%</p>
                      </div>
                    </div>
                  ))}
                  <div className="border-t pt-3 flex justify-between font-bold text-sm">
                    <span>Total Expenses</span>
                    <span className="text-red-600">
                      {formatCurrency(expenseBreakdown.reduce((sum, i) => sum + i.value, 0))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Properties Tab */}
        <TabsContent value="properties" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-sm sm:text-base">
                <BarChart3 className="h-4 w-4 mr-2 text-indigo-600" />
                Property Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="px-1 sm:px-6">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={propertyPerformance} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#6b7280" tickFormatter={(v) => `$${v/1000}k`} tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" stroke="#6b7280" width={80} tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Bar dataKey="revenue" fill="#6366f1" name="Revenue" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="profit" fill="#10b981" name="Profit" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {propertyPerformance.map((property, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-3 sm:pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm truncate">{property.name}</h4>
                    <Badge variant={property.occupancy >= 90 ? "default" : "secondary"} className="text-xs shrink-0 ml-1">
                      {property.occupancy}%
                    </Badge>
                  </div>
                  <div className="space-y-1.5 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Revenue</span>
                      <span className="font-medium text-emerald-600">{formatCurrency(property.revenue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Expenses</span>
                      <span className="font-medium text-red-600">{formatCurrency(property.expenses)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-1.5">
                      <span className="text-gray-700 font-medium">Net Profit</span>
                      <span className="font-bold text-blue-600">{formatCurrency(property.profit)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tax Tab */}
        <TabsContent value="tax" className="space-y-4 sm:space-y-6">
          {taxSummary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-3 sm:pt-4">
                    <p className="text-[10px] sm:text-xs text-purple-600 font-medium">Est. Annual Tax</p>
                    <p className="text-base sm:text-xl font-bold text-purple-700">{formatCurrency(taxSummary.estimatedTax)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-3 sm:pt-4">
                    <p className="text-[10px] sm:text-xs text-blue-600 font-medium">Quarterly</p>
                    <p className="text-base sm:text-xl font-bold text-blue-700">{formatCurrency(taxSummary.quarterlyPayment)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                  <CardContent className="p-3 sm:pt-4">
                    <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">Deductions</p>
                    <p className="text-base sm:text-xl font-bold text-emerald-700">{formatCurrency(taxSummary.deductions)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="p-3 sm:pt-4">
                    <p className="text-[10px] sm:text-xs text-amber-600 font-medium">Savings</p>
                    <p className="text-base sm:text-xl font-bold text-amber-700">{formatCurrency(taxSummary.potentialSavings)}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-sm sm:text-base">
                      <Calculator className="h-4 w-4 mr-2 text-purple-600" />
                      Tax Calculation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2.5 text-sm">
                      <div className="flex justify-between py-1.5 border-b">
                        <span className="text-gray-600">Gross Income</span>
                        <span className="font-medium">{formatCurrency(kpis.totalRevenue)}</span>
                      </div>
                      <div className="flex justify-between py-1.5 border-b">
                        <span className="text-gray-600">Operating Expenses</span>
                        <span className="font-medium text-red-600">-{formatCurrency(kpis.totalExpenses)}</span>
                      </div>
                      <div className="flex justify-between py-1.5 border-b">
                        <span className="text-gray-600">Deductions</span>
                        <span className="font-medium text-emerald-600">-{formatCurrency(taxSummary.deductions)}</span>
                      </div>
                      <div className="flex justify-between py-1.5 border-b font-medium">
                        <span>Taxable Income</span>
                        <span>{formatCurrency(taxSummary.taxableIncome)}</span>
                      </div>
                      <div className="flex justify-between py-1.5 border-b">
                        <span className="text-gray-600">Effective Rate</span>
                        <span className="font-medium">{taxSummary.effectiveRate}%</span>
                      </div>
                      <div className="flex justify-between py-1.5 font-bold text-base">
                        <span>Estimated Tax</span>
                        <span className="text-purple-600">{formatCurrency(taxSummary.estimatedTax)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-sm sm:text-base">
                      <Target className="h-4 w-4 mr-2 text-emerald-600" />
                      Tax Saving Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-emerald-600 mr-2 shrink-0" />
                          <span className="font-medium text-emerald-800 text-sm">Depreciation</span>
                        </div>
                        <p className="text-xs text-emerald-600 mt-1 ml-6">
                          Claim up to $45,000 in property depreciation
                        </p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center">
                          <AlertTriangle className="h-4 w-4 text-amber-600 mr-2 shrink-0" />
                          <span className="font-medium text-amber-800 text-sm">Missing Receipts</span>
                        </div>
                        <p className="text-xs text-amber-600 mt-1 ml-6">
                          12 expenses from Q3 missing - potential $3,200 deduction
                        </p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-blue-600 mr-2 shrink-0" />
                          <span className="font-medium text-blue-800 text-sm">Next Deadline</span>
                        </div>
                        <p className="text-xs text-blue-600 mt-1 ml-6">
                          Q1 2026 estimated tax: {taxSummary.nextDeadline}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* QB/Sage Integrations Tab */}
        <TabsContent value="integrations" className="space-y-4 sm:space-y-6" data-testid="integrations-panel">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-sm sm:text-base">
                <Link2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600" />
                Accounting Integrations
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Connect your accounting software for seamless data sync and export
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* QuickBooks Card */}
                <div className="border-2 rounded-xl p-4 sm:p-6 hover:border-green-300 transition-colors" data-testid="quickbooks-card">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-green-600 text-white flex items-center justify-center text-lg sm:text-xl font-bold mr-3">
                        QB
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm sm:text-base">QuickBooks</h3>
                        <p className="text-xs text-gray-500">Desktop & Online</p>
                      </div>
                    </div>
                    {connections.find(c => c.provider === 'quickbooks') ? (
                      <Badge className="bg-green-100 text-green-800 text-xs">Connected</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">Not Connected</Badge>
                    )}
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1.5">
                      <p className="flex items-start">
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 mr-1.5 mt-0.5 shrink-0" />
                        Export IIF files for QuickBooks Desktop
                      </p>
                      <p className="flex items-start">
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 mr-1.5 mt-0.5 shrink-0" />
                        CSV import for QuickBooks Online
                      </p>
                      <p className="flex items-start">
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 mr-1.5 mt-0.5 shrink-0" />
                        Auto-sync transactions (coming soon)
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      size="sm" className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm flex-1"
                      onClick={() => handleExport('quickbooks')}
                      disabled={exporting}
                      data-testid="qb-export-btn"
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Export IIF
                    </Button>
                    <Button
                      size="sm" variant="outline" className="text-xs sm:text-sm flex-1"
                      onClick={() => handleConnect('quickbooks')}
                      disabled={connectingProvider === 'quickbooks'}
                      data-testid="qb-connect-btn"
                    >
                      {connectingProvider === 'quickbooks' ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Link2 className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      {connections.find(c => c.provider === 'quickbooks') ? 'Reconnect' : 'Connect'}
                    </Button>
                  </div>
                </div>

                {/* Sage Card */}
                <div className="border-2 rounded-xl p-4 sm:p-6 hover:border-emerald-300 transition-colors" data-testid="sage-card">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-emerald-700 text-white flex items-center justify-center text-lg sm:text-xl font-bold mr-3">
                        S
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm sm:text-base">Sage</h3>
                        <p className="text-xs text-gray-500">Sage 50 & Sage Business</p>
                      </div>
                    </div>
                    {connections.find(c => c.provider === 'sage') ? (
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">Connected</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">Not Connected</Badge>
                    )}
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1.5">
                      <p className="flex items-start">
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500 mr-1.5 mt-0.5 shrink-0" />
                        Export Sage-compatible CSV
                      </p>
                      <p className="flex items-start">
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500 mr-1.5 mt-0.5 shrink-0" />
                        Nominal codes & department mapping
                      </p>
                      <p className="flex items-start">
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500 mr-1.5 mt-0.5 shrink-0" />
                        Auto-sync transactions (coming soon)
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-xs sm:text-sm flex-1"
                      onClick={() => handleExport('sage')}
                      disabled={exporting}
                      data-testid="sage-export-btn"
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Export CSV
                    </Button>
                    <Button
                      size="sm" variant="outline" className="text-xs sm:text-sm flex-1"
                      onClick={() => handleConnect('sage')}
                      disabled={connectingProvider === 'sage'}
                      data-testid="sage-connect-btn"
                    >
                      {connectingProvider === 'sage' ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Link2 className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      {connections.find(c => c.provider === 'sage') ? 'Reconnect' : 'Connect'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Quick Export Section */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-medium text-sm mb-3 flex items-center">
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-gray-600" />
                  Quick Export
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Button variant="outline" size="sm" className="text-xs h-auto py-2"
                    onClick={() => handleExport('csv', 'general_ledger')} disabled={exporting}
                    data-testid="quick-export-gl">
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    General Ledger
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs h-auto py-2"
                    onClick={() => handleExport('csv', 'income_statement')} disabled={exporting}
                    data-testid="quick-export-is">
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    Income Stmt
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs h-auto py-2"
                    onClick={() => handleExport('quickbooks')} disabled={exporting}
                    data-testid="quick-export-qb">
                    <span className="w-3.5 h-3.5 mr-1 rounded bg-green-600 text-white text-[8px] font-bold flex items-center justify-center shrink-0">Q</span>
                    QB IIF
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs h-auto py-2"
                    onClick={() => handleExport('sage')} disabled={exporting}
                    data-testid="quick-export-sage">
                    <span className="w-3.5 h-3.5 mr-1 rounded bg-emerald-700 text-white text-[8px] font-bold flex items-center justify-center shrink-0">S</span>
                    Sage CSV
                  </Button>
                </div>
              </div>

              {/* OAuth Notice */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs sm:text-sm text-blue-800">
                  <strong>Coming Soon:</strong> Direct OAuth integration with QuickBooks Online and Sage Business Cloud
                  for real-time transaction sync. Currently you can export data files for manual import.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancialAnalytics;
